import React, { lazy, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import hotkeys from 'hotkeys-js';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import styled from '@emotion/styled';
import Fade from '@mui/material/Fade';
import { isLocalizedDomain, getLocalizedPath } from 'helpers/getLocale';
const Scrollbar = React.lazy(() => import('components/Scrollbar'));
const QuestionsComponent = lazy(() => import('components/Questions'));
const ShortInfoComponent = lazy(() => import('./../components/ShortInfo'));
const SkillsSlideComponent = lazy(() => import('./../components/SkillsSlide'));
const ResponsibilitiesSlideComponent = lazy(() => import('./../components/ResponsibilitiesSlide'));
const DescriptionSlideComponent = lazy(() => import('./../components/DescriptionSlide'));
const PageNavigation = lazy(() => import('./../components/PageNavigation'));
const ScheduleSlideComponent = lazy(() => import('./../components/ScheduleSlide'));
const BenefitsSlideComponent = lazy(() => import('./../components/BenefitsSlide'));
const VacanciesComponent = lazy(() => import('./../components/Vacancies'));
const StepsToSwitchSlideComponent = lazy(() => import('./../components/StepsToSwitchSlide'));
const InstitutionsComponent = lazy(() => import('./../components/Institutions'));
const Sponsorship = lazy(() => import('./../components/Sponsorship'));
const SimilarProfessionsSlideComponent = lazy(
  () => import('./../components/SimilarProfessionsSlide')
);
const AiRiskSlideComponent = lazy(() => import('./../components/AiRiskSlide'));
const SlideHeader = lazy(() => import('./../components/SlideHeader'));
const SlideFooter = lazy(() => import('./../components/SlideFooter'));
const SlideEmptyState = lazy(() => import('./../components/SlideEmptyState'));
const ProfitrollLogo = lazy(() => import('assets/images/profitroll-logo-white.svg?react'));
const JobaltoLogo = lazy(() => import('assets/icons/jobatlo-logo.svg?react'));
const Burger = lazy(() => import('assets/icons/burger.svg?react'));

// Empty-state illustration icons — lazy so the bundle doesn't pay for them
// up front. Each slide that can render empty owns one.
const SuitcaseIcon = lazy(() => import('assets/icons/ri_suitcase-line.svg?react'));
const ChatIcon = lazy(() => import('assets/icons/ph_chat.svg?react'));
const PenIcon = lazy(() => import('assets/icons/iconamoon_edit-fill.svg?react'));
const HatIcon = lazy(() => import('assets/icons/educator_hat.svg?react'));
const AskIcon = lazy(() => import('assets/icons/gg_add.svg?react'));

const Wrapper = styled('div')(({ theme }) => ({
  paddingBottom: 0,
  height: '100vh',
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    height: '100vh',
    flexDirection: 'column',
    overflow: 'hidden'
  }
}));

const LeftSideBar = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  padding: '12px 12px 20px',
  width: 220,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${theme.palette.grey[100]}`,
  [theme.breakpoints.down('sm')]: {
    display: 'none'
  }
}));

const RightSideBar = styled('div')(({ theme }) => ({
  flex: 1,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    minHeight: 0
  }
}));

const SlideBody = styled('div')(() => ({
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column'
}));

const SlideContent = styled('div')(({ theme }) => ({
  flex: 1,
  width: '100%',
  maxWidth: 1240,
  margin: '0 auto',
  padding: '4px 24px 0',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  minHeight: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    padding: '4px 16px 0',
    minHeight: 'auto'
  }
}));

const InnerScroll = styled('div')(() => ({
  flex: 1,
  width: '100%',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden'
}));

// Shared bordered surface for slides that should read as a single anchored
// card (Skills, Steps, Education, Vacancies). Mirrors the QA / Advice card
// in the Questions component so the slide deck has one consistent
// surface treatment.
const SlideCard = styled('div')(({ theme }) => ({
  borderRadius: 15,
  border: `1px solid ${theme.palette.grey[100]}`,
  backgroundColor: theme.palette.white.main,
  marginTop: 16,
  marginBottom: 16,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flex: 1
  }
}));

const LogoWrapper = ({ className, onClick }) => {
  const LogoComponent = isLocalizedDomain() ? JobaltoLogo : ProfitrollLogo;
  return <LogoComponent className={className} onClick={onClick} />;
};

const LogoStyled = styled(LogoWrapper)(() => ({
  cursor: 'pointer',
  marginBottom: 18,
  marginLeft: 6,
  filter: 'brightness(0)',
  width: 120,
  height: 35,
  display: 'block',
  '& svg': {
    width: '100%',
    height: '100%'
  }
}));

// Mobile horizontal chip strip — replaces the verbose nav list that was
// pushing slide content off-screen at small widths. Sticks under the header
// so users can swipe-scroll between slides without re-opening a menu.
const MobileChipBar = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '10px 12px',
    backgroundColor: theme.palette.white.main,
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' }
  }
}));

const Chip = styled('button')(({ theme, active }) => ({
  flexShrink: 0,
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: active ? 600 : 500,
  padding: '6px 14px',
  borderRadius: 999,
  background: active ? theme.palette.primary.main : theme.palette.grey[100],
  color: active ? theme.palette.white.main : theme.palette.black.main,
  whiteSpace: 'nowrap',
  transition: 'background 160ms ease'
}));

const ContentElement = ({ children, slideKey }) => {
  // Brief fade on every slide change so the transition lands as a "slide"
  // rather than an instant page swap. Key on slideKey forces remount so the
  // Fade enter animation actually plays.
  return (
    <Fade in={true} key={slideKey} timeout={220}>
      <InnerScroll>
        <Scrollbar options={{ suppressScrollX: true }}>
          <SlideContent>{children}</SlideContent>
        </Scrollbar>
      </InnerScroll>
    </Fade>
  );
};

// Mapping of slide numbers to descriptive names
const slideNames = {
  0: 'overview',
  1: 'description',
  2: 'skills',
  3: 'schedule',
  4: 'responsibilities',
  5: 'benefits',
  6: 'questions',
  7: 'steps',
  8: 'education',
  9: 'vacancies',
  10: 'qa',
  11: 'similar',
  12: 'ai-risk'
};

// Short labels for the slide header + mobile chips. Aligned with the
// PageNavigation item list so the sidebar pill and the title bar always
// agree (slide 0 is labelled "Salary" because the overview surface is
// ShortInfoComponent, which leads with the salary stats).
const shortTitleKeys = {
  0: 'shortOverview',
  1: 'shortWhoIs',
  2: 'shortSkills',
  3: 'shortSchedule',
  4: 'shortDuties',
  5: 'shortProsAndCons',
  6: 'shortReviews',
  7: 'shortHowToBecome',
  8: 'shortEducation',
  9: 'shortVacancies',
  10: 'shortFaq',
  11: 'shortSimilar',
  12: 'shortAiRisk'
};

// Helper function to check if a slide has content. Vacancies / Questions /
// Q&A / Education always return true — the slide renders a beautified
// empty state when the data list is empty, so the user can still navigate
// to that section and see what's there.
const hasSlideContent = (step, professionInfo, vacanciesList) => {
  switch (step) {
    case 0:
    case 1:
      return true;
    case 2:
      return (professionInfo?.skills || []).length > 0;
    case 3:
      return professionInfo?.workEnvironment && professionInfo.workEnvironment.trim().length > 0;
    case 4:
      return (professionInfo?.workResponsibilities || []).length > 0;
    case 5: {
      const advantages = professionInfo?.advantages || [];
      const disadvantages = professionInfo?.disadvantages || [];
      return advantages.length > 0 || disadvantages.length > 0;
    }
    case 6:
      return true;
    case 7:
      return true;
    case 8:
      return true; // Education slide always shown — uses empty state when no programs
    case 9:
      return true; // Vacancies slide always shown — uses empty state when no listings
    case 10:
      return true;
    case 11:
      return true;
    case 12: {
      const previewAiRisk =
        typeof window !== 'undefined' && /[?&]previewAiRisk=1/.test(window.location.search);
      return !!professionInfo?.aiRisk || previewAiRisk;
    }
    default:
      return true;
  }
};

// Sidebar / navigation order — matches PageNavigation's item list, not the
// numeric slideNames map. AI-risk (12) is placed between description (1) and
// skills (2) so Next/Prev follow the visible sidebar order.
const SLIDE_ORDER = [0, 1, 12, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const getNextAvailableStep = (currentStep, professionInfo, vacanciesList) => {
  const idx = SLIDE_ORDER.indexOf(currentStep);
  if (idx === -1) return null;
  for (let i = idx + 1; i < SLIDE_ORDER.length; i++) {
    const step = SLIDE_ORDER[i];
    if (hasSlideContent(step, professionInfo, vacanciesList)) {
      return step;
    }
  }
  return null;
};

const getPreviousAvailableStep = (currentStep, professionInfo, vacanciesList) => {
  const idx = SLIDE_ORDER.indexOf(currentStep);
  if (idx === -1) return null;
  for (let i = idx - 1; i >= 0; i--) {
    const step = SLIDE_ORDER[i];
    if (hasSlideContent(step, professionInfo, vacanciesList)) {
      return step;
    }
  }
  return null;
};

const getAvailableSteps = (professionInfo, vacanciesList) => {
  return SLIDE_ORDER.filter((step) => hasSlideContent(step, professionInfo, vacanciesList));
};

const Slides = (props) => {
  const {
    t,
    professionInfo,
    handleToggleFavorite,
    highlight,
    handleChangeView,
    isFavorite,
    handleChooseTag,
    breadcrumbs,
    sponsor,
    loading,
    handleSetVisible,
    vacanciesList,
    handleGetConsultation,
    id
  } = props;

  const tNav = useTranslate('ProfessionNavigation');
  const tProf = useTranslate('Profession');
  const tQ = useTranslate('Questions');

  const navigate = useNavigate();
  const { slide, slug: routeSlug, id: routeId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useSelector((state) => state.app.isMobile);
  const chipBarRef = useRef(null);

  // Reverse mapping for URL to slide number
  const slideNumbers = useMemo(
    () => Object.fromEntries(Object.entries(slideNames).map(([num, name]) => [name, parseInt(num)])),
    []
  );

  // Update URL when slide changes
  const updateSlideUrl = useCallback(
    (step) => {
      // Route params are the source of truth: `professions/:slug/:id/:slide`
      // also matches under the localized `/:lc/*` layout, so useParams returns
      // the slug/id regardless of locale prefix.
      const finalSlug = routeSlug || professionInfo?.slug;
      const finalId = routeId || id || professionInfo?._id;

      if (!finalSlug || !finalId) {
        return;
      }

      const slideName = slideNames[step];
      if (!slideName) return;

      const finalExpectedPath = getLocalizedPath(
        `/professions/${finalSlug}/${finalId}/${slideName}`
      );

      if (window.location.pathname !== finalExpectedPath) {
        navigate(finalExpectedPath);
      }
    },
    [navigate, id, professionInfo, routeSlug, routeId]
  );

  // Read slide from URL on component mount
  useEffect(() => {
    if (slide) {
      const slideNumber = slideNumbers[slide];
      if (slideNumber !== undefined && slideNumber >= 0 && slideNumber <= 12) {
        if (hasSlideContent(slideNumber, professionInfo, vacanciesList)) {
          setActiveStep(slideNumber);
        } else {
          const nextStep = getNextAvailableStep(slideNumber, professionInfo, vacanciesList);
          const targetStep = nextStep !== null ? nextStep : 0;
          setActiveStep(targetStep);
          updateSlideUrl(targetStep);
        }
      } else {
        setActiveStep(0);
      }
    } else {
      setActiveStep(0);
    }
  }, [slide, slideNumbers, professionInfo, vacanciesList, updateSlideUrl]);

  // Ensure we're on a slide URL when component mounts
  useEffect(() => {
    if (professionInfo && !slide) {
      updateSlideUrl(0);
    }
  }, [professionInfo, slide, updateSlideUrl]);

  const goNext = useCallback(() => {
    const nextStep = getNextAvailableStep(activeStep, professionInfo, vacanciesList);
    if (nextStep !== null) {
      setActiveStep(nextStep);
      updateSlideUrl(nextStep);
    }
  }, [activeStep, professionInfo, vacanciesList, updateSlideUrl]);

  const goPrev = useCallback(() => {
    const prevStep = getPreviousAvailableStep(activeStep, professionInfo, vacanciesList);
    if (prevStep !== null) {
      setActiveStep(prevStep);
      updateSlideUrl(prevStep);
    }
  }, [activeStep, professionInfo, vacanciesList, updateSlideUrl]);

  React.useEffect(() => {
    hotkeys('right', goNext);
    hotkeys('left', goPrev);
    hotkeys('escape', () => handleChangeView(false));
    return () => {
      hotkeys.unbind('right');
      hotkeys.unbind('left');
      hotkeys.unbind('esc');
    };
  }, [goNext, goPrev, handleChangeView]);

  const availableSteps = useMemo(
    () => getAvailableSteps(professionInfo, vacanciesList),
    [professionInfo, vacanciesList]
  );
  const currentIndex = availableSteps.indexOf(activeStep);
  const nextStep = useMemo(
    () => getNextAvailableStep(activeStep, professionInfo, vacanciesList),
    [activeStep, professionInfo, vacanciesList]
  );
  const prevStep = useMemo(
    () => getPreviousAvailableStep(activeStep, professionInfo, vacanciesList),
    [activeStep, professionInfo, vacanciesList]
  );

  const slideTitle = useMemo(
    () => tProf('OccupationLabel', { name: professionInfo?.name || '' }),
    [tProf, professionInfo]
  );

  // Keep the active mobile chip scrolled into view so the user always sees
  // the current step even if it falls off the visible strip.
  useEffect(() => {
    if (!chipBarRef.current) return;
    const active = chipBarRef.current.querySelector('[data-active="true"]');
    if (active && active.scrollIntoView) {
      active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeStep]);

  // Build empty-state elements for slides that can render with no data.
  // Centralised here so all four slides share the same illustration system.
  const vacanciesEmpty = (
    <SlideEmptyState
      icon={<SuitcaseIcon />}
      title={tProf('EmptyVacanciesTitle')}
      description={tProf('EmptyVacanciesText')}
      actionLabel={tProf('EmptyVacanciesCta')}
      onAction={() => navigate(getLocalizedPath('/vacancies'))}
      tone="primary"
    />
  );

  const adviceEmpty = (
    <SlideEmptyState
      icon={<PenIcon />}
      title={tQ('EmptyAdviceTitle')}
      description={tQ('EmptyAdviceText')}
      actionLabel={tQ('GiveAdvice')}
      actionIcon={<AskIcon />}
      tone="primary"
    />
  );

  const questionsEmpty = (
    <SlideEmptyState
      icon={<ChatIcon />}
      title={tQ('EmptyQuestionTitle')}
      description={tQ('EmptyQuestionText')}
      actionLabel={tQ('AskQuestion')}
      actionIcon={<AskIcon />}
      tone="accent"
    />
  );

  const educationEmpty = (
    <SlideEmptyState
      icon={<HatIcon />}
      title={tProf('EmptyEducationTitle')}
      description={tProf('EmptyEducationText')}
      tone="neutral"
    />
  );

  return (
    <Wrapper>
      <LeftSideBar>
        <LogoStyled onClick={handleChangeView} />
        <PageNavigation
          handleClick={(step) => {
            if (hasSlideContent(step, professionInfo, vacanciesList)) {
              setActiveStep(step);
              updateSlideUrl(step);
            }
          }}
          professionInfo={professionInfo}
          highlight={highlight}
          handleChangeView={handleChangeView}
          slides={true}
          activeStep={activeStep}
          menuOpened={true}
          handleCloseMenu={() => {}}
          availableSteps={availableSteps}
        />
      </LeftSideBar>

      <RightSideBar>
        <SlideHeader
          title={slideTitle}
          onExit={handleChangeView}
          exitLabel={tProf('ExitSlideMode')}
        />

        <MobileChipBar ref={chipBarRef}>
          {availableSteps.map((step) => {
            const key = shortTitleKeys[step];
            const label = key ? tNav(key) : `Slide ${step}`;
            const isActive = step === activeStep;
            return (
              <Chip
                key={step}
                active={isActive}
                data-active={isActive ? 'true' : 'false'}
                onClick={() => {
                  setActiveStep(step);
                  updateSlideUrl(step);
                }}>
                {label}
              </Chip>
            );
          })}
        </MobileChipBar>

        <SlideBody>
          {activeStep === 0 ? (
            <ContentElement slideKey="overview">
              <Sponsorship sponsor={sponsor} professionInfo={professionInfo} />
              <ShortInfoComponent
                professionInfo={professionInfo}
                handleToggleFavorite={handleToggleFavorite}
                isFavorite={isFavorite}
                t={t}
                handleChooseTag={handleChooseTag}
                breadcrumbs={breadcrumbs}
                sponsor={sponsor}
                loading={loading}
                highlight={highlight}
                slides={true}
              />
            </ContentElement>
          ) : null}

          {activeStep === 1 ? (
            <ContentElement slideKey="description">
              <SlideCard>
                <DescriptionSlideComponent professionInfo={professionInfo} t={t} />
              </SlideCard>
            </ContentElement>
          ) : null}

          {activeStep === 2 ? (
            <ContentElement slideKey="skills">
              <SlideCard>
                <SkillsSlideComponent professionInfo={professionInfo} t={t} />
              </SlideCard>
            </ContentElement>
          ) : null}

          {activeStep === 3 ? (
            <ContentElement slideKey="schedule">
              <SlideCard>
                <ScheduleSlideComponent professionInfo={professionInfo} t={t} />
              </SlideCard>
            </ContentElement>
          ) : null}

          {activeStep === 4 ? (
            <ContentElement slideKey="responsibilities">
              <SlideCard>
                <ResponsibilitiesSlideComponent professionInfo={professionInfo} t={t} />
              </SlideCard>
            </ContentElement>
          ) : null}

          {activeStep === 5 ? (
            <ContentElement slideKey="benefits">
              <BenefitsSlideComponent professionInfo={professionInfo} t={t} />
            </ContentElement>
          ) : null}

          {activeStep === 6 ? (
            <ContentElement slideKey="advice">
              <QuestionsComponent
                professionInfo={professionInfo}
                t={t}
                entity={'reviews'}
                type={'advices'}
                setIsVisible={handleSetVisible}
                emptyState={adviceEmpty}
                hideHeader={true}
              />
            </ContentElement>
          ) : null}

          {activeStep === 7 ? (
            <ContentElement slideKey="steps">
              <SlideCard>
                <StepsToSwitchSlideComponent professionInfo={professionInfo} t={t} />
              </SlideCard>
            </ContentElement>
          ) : null}

          {activeStep === 8 ? (
            <ContentElement slideKey="education">
              <SlideCard>
                {(professionInfo?.educationPrograms || []).length === 0 ? (
                  educationEmpty
                ) : (
                  <InstitutionsComponent
                    professionInfo={professionInfo}
                    t={t}
                    setIsVisible={handleSetVisible}
                  />
                )}
              </SlideCard>
            </ContentElement>
          ) : null}

          {activeStep === 9 ? (
            <ContentElement slideKey="vacancies">
              <Sponsorship sponsor={sponsor} professionInfo={professionInfo} />
              <VacanciesComponent
                professionInfo={professionInfo}
                t={t}
                vacanciesList={vacanciesList}
                setIsVisible={handleSetVisible}
                highlight={highlight}
                title={professionInfo?.name}
                slides={true}
                emptyState={vacanciesEmpty}
              />
            </ContentElement>
          ) : null}

          {activeStep === 10 ? (
            <ContentElement slideKey="qa">
              <QuestionsComponent
                entity={'questions'}
                setIsVisible={handleSetVisible}
                emptyState={questionsEmpty}
                hideHeader={true}
              />
            </ContentElement>
          ) : null}

          {activeStep === 11 ? (
            <ContentElement slideKey="similar">
              <SimilarProfessionsSlideComponent professionInfo={professionInfo} t={t} />
            </ContentElement>
          ) : null}

          {activeStep === 12 ? (
            <ContentElement slideKey="ai-risk">
              <AiRiskSlideComponent professionInfo={professionInfo} t={t} />
            </ContentElement>
          ) : null}
        </SlideBody>

        <SlideFooter
          current={currentIndex + 1}
          total={availableSteps.length}
          onPrev={prevStep !== null ? goPrev : null}
          onNext={nextStep !== null ? goNext : null}
          prevLabel={tProf('Prev')}
          nextLabel={tProf('Next')}
          hintLabel={tProf('SlideKeyHint')}
        />
      </RightSideBar>
    </Wrapper>
  );
};

export default Slides;
